/**
 * Hook para gerenciar eventos SSE de sincronização
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api`;

/**
 * Todas as queryKeys que devem ser invalidadas/refetchadas ao sincronizar
 */
const ALL_DATA_QUERY_KEYS = [
  ['units'],
  ['documents'],
  ['processes'],
  ['notifications'],
  ['tasks'],
] as const;

/**
 * Função helper para invalidar todas as queries de dados
 */
function invalidateAllDataQueries(queryClient: QueryClient) {
  return Promise.all(
    ALL_DATA_QUERY_KEYS.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    )
  );
}

/**
 * Função helper para refetch todas as queries de dados (mais rápido que invalidate)
 */
function refetchAllDataQueries(queryClient: QueryClient) {
  return Promise.all(
    ALL_DATA_QUERY_KEYS.map((queryKey) =>
      queryClient.refetchQueries({ queryKey, type: 'active' })
    )
  );
}

export type SyncEventType = 'sync:start' | 'sync:progress' | 'sync:complete' | 'sync:error' | 'heartbeat';

export interface SyncEvent {
  type: SyncEventType;
  timestamp: string;
  data?: {
    endpoint?: string;
    current?: number;
    total?: number;
    duration?: number;
    error?: string;
    lastSync?: string;
    counts?: {
      units: number;
      documents: number;
      processes: number;
      notifications: number;
      tasks: number;
    };
  };
}

export interface SyncStatus {
  isConnected: boolean;
  isSyncing: boolean;
  lastSync: string | null;
  currentProgress: { current: number; total: number } | null;
  lastError: string | null;
}

/**
 * Hook para conectar-se ao stream SSE e receber eventos de sync
 */
export function useSyncEvents() {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [status, setStatus] = useState<SyncStatus>({
    isConnected: false,
    isSyncing: false,
    lastSync: null,
    currentProgress: null,
    lastError: null,
  });

  const [events, setEvents] = useState<SyncEvent[]>([]);

  const handleEvent = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data) as SyncEvent;
      
      // Adiciona evento ao histórico (máximo 50)
      setEvents((prev) => [data, ...prev].slice(0, 50));

      // Atualiza status baseado no tipo do evento
      switch (data.type) {
        case 'sync:start':
          setStatus((prev) => ({
            ...prev,
            isSyncing: true,
            currentProgress: { current: 0, total: data.data?.total || 6 },
            lastError: null,
          }));
          break;

        case 'sync:progress':
          setStatus((prev) => ({
            ...prev,
            currentProgress: {
              current: data.data?.current || 0,
              total: data.data?.total || 6,
            },
          }));
          break;

        case 'sync:complete':
          setStatus((prev) => ({
            ...prev,
            isSyncing: false,
            lastSync: data.timestamp,
            currentProgress: null,
          }));
          // Invalida TODAS as queries de dados para recarregar
          invalidateAllDataQueries(queryClient);
          break;

        case 'sync:error':
          setStatus((prev) => ({
            ...prev,
            isSyncing: false,
            currentProgress: null,
            lastError: data.data?.error || 'Erro desconhecido',
          }));
          break;

        case 'heartbeat':
          // Apenas confirma que a conexão está ativa
          break;
      }
    } catch (error) {
      console.error('Erro ao processar evento SSE:', error);
    }
  }, [queryClient]);

  const connect = useCallback(() => {
    // Limpa conexão existente
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Cria nova conexão SSE
    const eventSource = new EventSource(`${API_BASE}/events`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setStatus((prev) => ({ ...prev, isConnected: true }));
      console.log('[SSE] Conectado ao stream de eventos');
    };

    eventSource.onmessage = handleEvent;

    eventSource.onerror = () => {
      setStatus((prev) => ({ ...prev, isConnected: false }));
      console.warn('[SSE] Conexão perdida, tentando reconectar...');
      
      // Tenta reconectar após 5 segundos
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, 5000);
    };
  }, [handleEvent]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setStatus((prev) => ({ ...prev, isConnected: false }));
  }, []);

  // Conecta ao montar e desconecta ao desmontar
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    status,
    events,
    connect,
    disconnect,
  };
}

/**
 * Hook para disparar sync manual com refresh instantâneo
 * 
 * Fluxo otimizado:
 * 1. Refresh instantâneo: Invalida cache local e refetch dados do servidor (ms)
 * 2. Sync em background: Dispara scraping do PropertyDocs (segundos/minutos)
 * 3. Auto-update: Quando sync completa, SSE notifica e dados são atualizados
 */
export function useManualSync() {
  const queryClient = useQueryClient();
  const [isTriggering, setIsTriggering] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Refresh instantâneo - apenas recarrega dados do cache do servidor
   * Sem fazer scraping externo, executa em milissegundos
   */
  const instantRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Primeiro, pedir ao servidor para limpar seu cache interno
      await fetch(`${API_BASE}/sync/refresh`, { method: 'POST' }).catch(() => {
        // Endpoint opcional, continua mesmo se não existir
      });
      
      // Refetch todas as queries ativas (instantâneo)
      await refetchAllDataQueries(queryClient);
      return true;
    } catch (err) {
      console.error('Erro no refresh instantâneo:', err);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  /**
   * Sync completo - dispara scraping em background e faz refresh instantâneo
   */
  const triggerSync = useCallback(async () => {
    setIsTriggering(true);
    setError(null);

    try {
      // 1. Refresh instantâneo PRIMEIRO - UX imediata
      await instantRefresh();
      
      // 2. Dispara sync em background (não espera completar)
      const response = await fetch(`${API_BASE}/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao iniciar sincronização');
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(message);
      return false;
    } finally {
      setIsTriggering(false);
    }
  }, [instantRefresh]);

  return {
    triggerSync,
    instantRefresh,
    isTriggering,
    isRefreshing,
    error,
  };
}
